import React, { useEffect, useRef, useState } from 'react';
import { useGetIdentity, useNotify } from 'react-admin';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiFetchResponse } from '../lib/apiFetch';
import { Contract } from '../types';
import { shortDate } from './preneedTimeline';

type Confirming = 'replace' | 'activate' | 'undo' | null;

/** The backend's own sentence ("Add the TruStage policy number…") over "API error: 400". */
async function call(path: string, init: RequestInit): Promise<Contract> {
  const res = await apiFetchResponse(`preneed/contracts/${path}`, {
    method: 'POST',
    ...init,
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const msg = Array.isArray(body?.message)
      ? body.message.join(', ')
      : body?.message;
    throw new Error(msg || `Something went wrong (${res.status})`);
  }
  return body as Contract;
}

/**
 * Manual TruStage actions (T-212), for when TruStage sends only a policy
 * number, or the certificate reaches us some way intake can't see: set the
 * policy number, upload (or replace) the certificate PDF, mark the plan
 * Active or undo it. Every action is logged on the case feed by the backend.
 */
export function TrustageCertificateDialog({
  contract,
  open,
  onClose,
  onChanged,
}: {
  contract: Contract;
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const by = identity?.fullName || undefined;
  const fileRef = useRef<HTMLInputElement>(null);
  const [number, setNumber] = useState(contract.trustage_certificate_number ?? '');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<Confirming>(null);

  useEffect(() => {
    if (open) {
      setNumber(contract.trustage_certificate_number ?? '');
      setConfirming(null);
    }
  }, [open, contract.trustage_certificate_number]);

  const active = contract.trustage_status === 'active';
  const hasPdf = !!contract.certificate_document_id;
  const savedNumber = contract.trustage_certificate_number ?? '';
  const numberChanged = number.trim() !== savedNumber;

  const run = async (key: string, fn: () => Promise<Contract>, done: string) => {
    setBusy(key);
    try {
      await fn();
      notify(done, { type: 'success' });
      setConfirming(null);
      onChanged();
    } catch (err: any) {
      notify(err?.message || 'Something went wrong', { type: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const saveNumber = () =>
    run(
      'number',
      () =>
        call(`${contract.id}/policy-number`, {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ policyNumber: number.trim() || null, by }),
        }),
      number.trim() ? 'Policy number saved' : 'Policy number cleared'
    );

  const upload = (file: File) => {
    const form = new FormData();
    form.append('file', file);
    if (by) form.append('by', by);
    if (numberChanged && number.trim()) form.append('policyNumber', number.trim());
    return run(
      'upload',
      () => call(`${contract.id}/upload-certificate`, { body: form }),
      hasPdf ? 'Certificate replaced' : 'Certificate filed: plan Active'
    );
  };

  const post = (key: string, path: string, done: string) =>
    run(
      key,
      () =>
        call(`${contract.id}/${path}`, {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ by }),
        }),
      done
    );

  const pickFile = () => fileRef.current?.click();

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="trustage-certificate-dialog">
        <DialogHeader>
          <DialogTitle>TruStage certificate</DialogTitle>
          <DialogDescription>
            For when TruStage sends only a policy number, or the certificate
            arrives some way the app can't see. Each change is noted on the
            case feed.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 text-[13px]">
          {/* Policy number */}
          <section>
            <label htmlFor="trustage-policy-number" className="font-medium">
              Policy number
            </label>
            <div className="mt-1 flex gap-2">
              <Input
                id="trustage-policy-number"
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="2022109384"
                className="h-8"
                autoComplete="off"
              />
              <Button
                size="sm"
                className="h-8"
                disabled={!numberChanged || !!busy}
                onClick={() => void saveNumber()}
              >
                {busy === 'number' ? 'Saving…' : 'Save'}
              </Button>
            </div>
            {savedNumber && (
              <p className="mt-1 text-[12px] text-muted-foreground">
                {contract.trustage_certificate_number_set_by
                  ? `Set by ${contract.trustage_certificate_number_set_by}, ${shortDate(
                      contract.trustage_certificate_number_set_at
                    )}`
                  : 'Read from the certificate'}
              </p>
            )}
          </section>

          {/* Certificate PDF */}
          <section>
            <div className="font-medium">Certificate PDF</div>
            <p className="mt-0.5 text-[12px] text-muted-foreground">
              {hasPdf
                ? `On file since ${shortDate(contract.certificate_received_at)} (Documents tab).`
                : active
                ? 'Not on file. Upload it when TruStage sends it.'
                : 'Not on file. Uploading files it on the case and makes the plan Active, as if TruStage had emailed it.'}
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              data-testid="trustage-certificate-file"
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                if (f) void upload(f);
              }}
            />
            {confirming === 'replace' ? (
              <ConfirmRow
                text="The current certificate is archived and replaced. The confirmation package isn't resent."
                action="Choose the new PDF"
                busy={busy === 'upload'}
                onConfirm={pickFile}
                onCancel={() => setConfirming(null)}
              />
            ) : (
              <Button
                size="sm"
                variant={hasPdf ? 'outline' : 'default'}
                className="mt-1.5 h-7 px-2.5 text-xs"
                disabled={!!busy}
                onClick={() => (hasPdf ? setConfirming('replace') : pickFile())}
              >
                {busy === 'upload'
                  ? 'Uploading…'
                  : hasPdf
                  ? 'Replace PDF'
                  : 'Upload certificate'}
              </Button>
            )}
          </section>

          {/* Status */}
          <section>
            <div className="font-medium">
              Status: {active ? 'Active' : contract.trustage_status === 'sent' ? 'At TruStage' : 'Not active'}
            </div>
            {active && contract.trustage_activated_by && !hasPdf && (
              <p className="mt-0.5 text-[12px] text-muted-foreground">
                Marked Active by {contract.trustage_activated_by},{' '}
                {shortDate(contract.trustage_activated_at)}.
              </p>
            )}
            {!active && confirming === 'activate' && (
              <ConfirmRow
                text={`Mark the plan Active with policy ${savedNumber}? The confirmation package still waits for the certificate PDF.`}
                action="Mark Active"
                busy={busy === 'activate'}
                onConfirm={() => void post('activate', 'mark-active', 'Plan marked Active')}
                onCancel={() => setConfirming(null)}
              />
            )}
            {active && confirming === 'undo' && (
              <ConfirmRow
                text={
                  hasPdf
                    ? 'Undo Active? The certificate is removed from the case and a scheduled confirmation package is cancelled.'
                    : 'Undo Active? The plan goes back to waiting for TruStage.'
                }
                action="Undo Active"
                busy={busy === 'undo'}
                onConfirm={() => void post('undo', 'undo-active', 'Active undone')}
                onCancel={() => setConfirming(null)}
              />
            )}
            {!confirming && !active && (
              <>
                <Button
                  size="sm"
                  className="mt-1.5 h-7 px-2.5 text-xs"
                  disabled={!savedNumber || numberChanged || !!busy}
                  onClick={() => setConfirming('activate')}
                >
                  Mark Active
                </Button>
                {!savedNumber && (
                  <p className="mt-1 text-[12px] text-muted-foreground">
                    Save the policy number first.
                  </p>
                )}
              </>
            )}
            {!confirming && active && (
              <Button
                size="sm"
                variant="outline"
                className="mt-1.5 h-7 px-2.5 text-xs"
                disabled={!!busy || !!contract.confirmation_package_sent_at}
                onClick={() => setConfirming('undo')}
              >
                Undo Active
              </Button>
            )}
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmRow({
  text,
  action,
  busy,
  onConfirm,
  onCancel,
}: {
  text: string;
  action: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="mt-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-[12px]">
      <div>{text}</div>
      <div className="mt-1.5 flex gap-1.5">
        <Button size="sm" className="h-7 px-2.5 text-xs" disabled={busy} onClick={onConfirm}>
          {busy ? 'Working…' : action}
        </Button>
        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
